import { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/feature/AppShell";
import ContactListView from "@/pages/contactos/components/ContactListView";
import ContactBoardView from "@/pages/contactos/components/ContactBoardView";
import ScrollFade from "@/components/base/ScrollFade";
import ContactDrawer from "@/pages/contactos/components/ContactDrawer";
import { pipelineStages, sideStages } from "@/mocks/prospectos";
import type { Prospect } from "@/pages/prospectar/components/ProspectCard";
import { statusDotStyles } from "@/components/feature/StatusBadge";
import { fetchProspects } from "@/services/prospects";
import { supabase } from "@/lib/supabaseClient";
import { Link } from "react-router-dom";

export default function Contactos() {
  const [view, setView] = useState<"lista" | "tablero">("lista");
  const [contacts, setContacts] = useState<Prospect[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [stageFilter, setStageFilter] = useState("Todas");
  const [dragId, setDragId] = useState<string | null>(null);
  const [overStage, setOverStage] = useState<string | null>(null);
  const [selected, setSelected] = useState<Prospect | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    fetchProspects()
      .then((data) => {
        if (active) setContacts(data);
      })
      .catch((err) => {
        if (active) setLoadError(err instanceof Error ? err.message : "No se pudo cargar tus contactos.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2400);
  };

  const patchContact = (id: string, patch: Partial<Prospect>) => {
    setContacts((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
    setSelected((s) => (s && s.id === id ? { ...s, ...patch } : s));
  };

  const move = (id: string, status: string) => {
    const moved = contacts.find((c) => c.id === id);
    setContacts((prev) => prev.map((c) => (c.id === id ? { ...c, status } : c)));
    setSelected((s) => (s && s.id === id ? { ...s, status } : s));
    if (moved && moved.status !== status) {
      showToast(`${moved.name} → ${status}`);
      // Guarda la nueva etapa en Supabase (optimista: ya se actualizó la UI arriba).
      supabase
        .from("prospects")
        .update({ pipeline_stage: status })
        .eq("id", id)
        .then(({ error }) => {
          if (error) showToast(`No se pudo guardar el cambio: ${error.message}`);
        });
    }
  };

  const listFiltered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return contacts.filter((c) => {
      if (q && !c.name.toLowerCase().includes(q) && !c.username.toLowerCase().includes(q)) return false;
      if (stageFilter === "Necesitan intervención") return !!c.needsHuman;
      if (stageFilter !== "Todas" && c.status !== stageFilter) return false;
      return true;
    });
  }, [contacts, query, stageFilter]);

  return (
    <AppShell headerVariant="onLight" showBack>
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-foreground-950">Contactos</h1>
            <p className="text-foreground-600 text-sm mt-1">
              Tu CRM sencillo: sigue a cada persona desde que llega hasta que se vuelve socio.
            </p>
            <Link to="/app/mi-whatsapp" className="inline-flex items-center gap-1.5 mt-2 text-sm font-semibold text-primary-700 hover:underline">
              <i className="ri-whatsapp-line"></i> Mi agente de WhatsApp
            </Link>
          </div>
          {/* View switcher */}
          <div className="inline-flex items-center gap-1 bg-background-100 rounded-full p-1 self-start md:self-auto">
            <button
              onClick={() => setView("lista")}
              className={[
                "px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 cursor-pointer whitespace-nowrap transition-colors",
                view === "lista" ? "bg-background-50 text-foreground-950" : "text-foreground-600 hover:text-foreground-900",
              ].join(" ")}
            >
              <i className="ri-list-check-2"></i> Lista
            </button>
            <button
              onClick={() => setView("tablero")}
              className={[
                "px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 cursor-pointer whitespace-nowrap transition-colors",
                view === "tablero" ? "bg-background-50 text-foreground-950" : "text-foreground-600 hover:text-foreground-900",
              ].join(" ")}
            >
              <i className="ri-layout-grid-line"></i> Tablero
            </button>
          </div>
        </div>

        {loading ? (
          <div className="mt-6 bg-background-100 border border-background-200 rounded-2xl p-10 text-center text-foreground-500">
            <i className="ri-loader-4-line animate-spin text-2xl"></i>
            <p className="mt-2">Cargando contactos desde Supabase…</p>
          </div>
        ) : loadError ? (
          <div className="mt-6 bg-background-100 border border-dashed border-accent-400 rounded-2xl p-10 text-center text-accent-700">
            {loadError}
          </div>
        ) : (
          <>
            {/* Totales */}
            <div className="mt-6 grid grid-cols-3 gap-3">
              <div className="bg-background-100 border border-background-200 rounded-2xl p-4">
                <div className="text-xs text-foreground-500">Prospectos</div>
                <div className="font-heading font-extrabold text-2xl text-foreground-950">{contacts.length}</div>
              </div>
              <button
                onClick={() => {
                  setView("lista");
                  setStageFilter(stageFilter === "Necesitan intervención" ? "Todas" : "Necesitan intervención");
                }}
                className={`text-left rounded-2xl p-4 border cursor-pointer ${contacts.some((c) => c.needsHuman) ? "bg-accent-500/10 border-accent-400/50" : "bg-background-100 border-background-200"}`}
              >
                <div className="text-xs text-foreground-500">Necesitan intervención</div>
                <div className="font-heading font-extrabold text-2xl text-accent-700">{contacts.filter((c) => c.needsHuman).length}</div>
              </button>
              <div className="bg-background-100 border border-background-200 rounded-2xl p-4">
                <div className="text-xs text-foreground-500">Ya son socios</div>
                <div className="font-heading font-extrabold text-2xl text-primary-700">{contacts.filter((c) => c.status === "Socio").length}</div>
              </div>
            </div>

            {/* Pipeline summary */}
            <ScrollFade
              containerClassName="mt-4 flex gap-2 overflow-x-auto pb-1 vv-scrollbar-hidden snap-x snap-mandatory scroll-px-1"
              ariaLabel="Resumen del embudo de etapas"
            >
              {[...pipelineStages, ...sideStages].map((s) => (
                <div key={s} className="flex-shrink-0 snap-start bg-background-100 border border-background-200 rounded-xl px-3 py-2 flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${statusDotStyles[s] ?? "bg-background-400"}`}></span>
                  <span className="text-xs text-foreground-600 whitespace-nowrap">{s}</span>
                  <span className="text-sm font-heading font-extrabold text-foreground-950">
                    {contacts.filter((c) => c.status === s).length}
                  </span>
                </div>
              ))}
            </ScrollFade>

            {view === "lista" ? (
              <>
                <div className="mt-5 flex flex-col sm:flex-row gap-2">
                  <div className="flex-1 flex items-center gap-2 bg-background-100 border border-background-200 rounded-full px-4 py-2.5">
                    <i className="ri-search-line text-foreground-400 text-sm"></i>
                    <input
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      type="text"
                      placeholder="Buscar contactos…"
                      className="w-full bg-transparent outline-none text-sm text-foreground-950"
                    />
                  </div>
                  <label className="flex items-center gap-2 bg-background-100 border border-background-200 rounded-full px-4 py-2.5">
                    <i className="ri-filter-3-line text-foreground-400 text-sm"></i>
                    <select
                      value={stageFilter}
                      onChange={(e) => setStageFilter(e.target.value)}
                      className="bg-transparent outline-none text-sm text-foreground-950 cursor-pointer"
                    >
                      {["Todas", "Necesitan intervención", ...pipelineStages, ...sideStages].map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="mt-4">
                  <ContactListView contacts={listFiltered} onOpen={setSelected} />
                </div>
              </>
            ) : (
              <>
                <div className="mt-5 rounded-2xl bg-secondary-500/10 border border-secondary-500/25 p-3 flex items-start gap-2 text-sm text-foreground-800">
                  <i className="ri-drag-move-line text-secondary-600 mt-0.5"></i>
                  <span>
                    <span className="hidden lg:inline">Arrastra las tarjetas entre etapas para actualizar el estado de cada contacto.</span>
                    <span className="lg:hidden">Toca una tarjeta para ver el perfil o usa el selector de <span className="font-semibold">Etapa</span> dentro de cada tarjeta para moverla.</span>
                  </span>
                </div>
                <div className="mt-4">
                  <ContactBoardView
                    contacts={contacts}
                    onMove={move}
                    onOpen={setSelected}
                    dragId={dragId}
                    setDragId={setDragId}
                    overStage={overStage}
                    setOverStage={setOverStage}
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>

      <ContactDrawer
        prospect={selected}
        onClose={() => setSelected(null)}
        onMove={move}
        onPatch={patchContact}
        onReassigned={(id) => setContacts((prev) => prev.filter((c) => c.id !== id))}
        notify={showToast}
      />

      {toast ? (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[60] bg-background-950 text-white text-sm px-4 py-3 rounded-full flex items-center gap-2">
          <i className="ri-checkbox-circle-fill text-primary-400"></i> {toast}
        </div>
      ) : null}
    </AppShell>
  );
}
